// src/pages/peta-estate-4/PetaEstate4.jsx
import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, Popup, useMap, ZoomControl, LayerGroup } from 'react-leaflet';
import {
  Box, Typography, CircularProgress, Alert, FormControl, InputLabel, 
  Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, DialogContentText , Slide,
  Switch, FormControlLabel
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SettingsIcon from '@mui/icons-material/Settings';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Import MapDataContext
import { MapDataContext } from './contexts/MapDataContext';
// Di bagian atas file PetaEstate4.jsx, setelah import lainnya
import "./css/PetaEstate4.css";

// Fix for default icon issues with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Custom icon for mandor
const mandorIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Custom icon for Pos Keamanan
const securityPostIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Komponen untuk menampilkan label lokasi menggunakan LayerGroup - VERSION FIXED
const LocationLabelsWithLayerGroup = ({ geojsonFeatures, showLabels }) => {
  const map = useMap();
  const layerGroupRef = useRef(L.layerGroup());

  useEffect(() => {
    if (!showLabels || !geojsonFeatures || !geojsonFeatures.features || geojsonFeatures.features.length === 0) {
      // Hapus layer jika tidak menampilkan label atau tidak ada features
      if (layerGroupRef.current) {
        map.removeLayer(layerGroupRef.current);
      }
      return;
    }

    // Hapus layer lama
    if (layerGroupRef.current) {
      map.removeLayer(layerGroupRef.current);
    }

    // Buat layer group baru
    layerGroupRef.current = L.layerGroup();
    
    geojsonFeatures.features.forEach(feature => {
      const properties = feature.properties || {};
      const lokasiCode = properties.lokasi;
      
      if (!lokasiCode || !feature.geometry || !feature.geometry.coordinates) return;
      
      let lat, lng;
      
      if (feature.geometry.type === 'Point') {
        [lng, lat] = feature.geometry.coordinates;
      } else if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
        // Untuk Polygon dan MultiPolygon: ambil centroid dari polygon pertama
        const coordinates = feature.geometry.type === 'Polygon' 
          ? feature.geometry.coordinates[0] 
          : feature.geometry.coordinates[0][0];
        
        let sumLng = 0;
        let sumLat = 0;
        let count = 0;
        
        coordinates.forEach(coord => {
          if (Array.isArray(coord) && coord.length >= 2) {
            sumLng += coord[0];
            sumLat += coord[1];
            count++;
          }
        });
        
        if (count > 0) {
          lng = sumLng / count;
          lat = sumLat / count;
        } else {
          return;
        }
      } else {
        return;
      }
      
      if (lat === undefined || lng === undefined || isNaN(lat) || isNaN(lng)) return;

      // Buat custom icon untuk label dengan ukuran yang tepat
      const labelIcon = L.divIcon({
        html: `<div style="
          background: transparent !important;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: bold;
          border: none !important;
          white-space: nowrap;
          text-align: center;
          pointer-events: none;
        ">${lokasiCode}</div>`,
        className: 'location-label-icon',
        iconSize: [null, null],
        iconAnchor: [0, 0]
      });

      // Buat marker dengan custom icon
      const marker = L.marker([lat, lng], {
        icon: labelIcon,
        interactive: false,
        keyboard: false,
        zIndexOffset: 1000
      });

      // Tambahkan marker ke layer group
      marker.addTo(layerGroupRef.current);
    });

    // Tambahkan layer group ke peta setelah semua marker dibuat
    layerGroupRef.current.addTo(map);

    // Cleanup function
    return () => {
      if (layerGroupRef.current) {
        map.removeLayer(layerGroupRef.current);
      }
    };
  }, [map, geojsonFeatures, showLabels]);

  return null;
};

// Custom Zoom Control dengan tombol settings di tengah
const CustomZoomControl = ({ onSettingsClick }) => {
  const map = useMap();
  
  return (
    <div className="leaflet-control leaflet-bar custom-zoom-control">
      <a 
        href="#" 
        title="Zoom in" 
        onClick={(e) => {
          e.preventDefault();
          map.zoomIn();
        }}
      >
        <AddIcon fontSize="small" />
      </a>
      
      <a 
        href="#" 
        title="Settings" 
        className="settings-button"
        onClick={(e) => {
          e.preventDefault();
          onSettingsClick();
        }}
      >
        <SettingsIcon fontSize="small" />
      </a>
      
      <a 
        href="#" 
        title="Zoom out" 
        onClick={(e) => {
          e.preventDefault();
          map.zoomOut();
        }}
      >
        <RemoveIcon fontSize="small" />
      </a>
    </div>
  );
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function PetaEstate4() {
  // State untuk komoditas yang dipilih - ambil dari localStorage jika ada
  const [commodity, setCommodity] = useState(
    () => localStorage.getItem('selectedCommodity') || 'None'
  );

  // State untuk tahun yang dipilih (khusus Banana)
  const [selectedYear, setSelectedYear] = useState(
    () => localStorage.getItem('selectedYear') || ''
  );

  // State untuk komoditas sementara di dialog
  const [tempCommodity, setTempCommodity] = useState(commodity);
  
  // State untuk tahun sementara di dialog (khusus Banana)
  const [tempYear, setTempYear] = useState(selectedYear);

  // Di dalam komponen PetaEstate4, tambahkan state untuk status yang dipilih
const [selectedStatus, setSelectedStatus] = useState(
  () => localStorage.getItem('selectedStatus') || ''
);

// State untuk status sementara di dialog
const [tempStatus, setTempStatus] = useState(selectedStatus);

// Handler untuk perubahan status sementara di dialog
const handleTempStatusChange = (event) => {
  setTempStatus(event.target.value);
};

  // State untuk dialog settings
  const [settingsOpen, setSettingsOpen] = useState(false);

    // State untuk filtered count di dialog
  const [tempFilteredCount, setTempFilteredCount] = useState(0);

  // Di dalam komponen PetaEstate4, tambahkan state untuk jenis penyakit yang dipilih
const [selectedDisease, setSelectedDisease] = useState(
  () => localStorage.getItem('selectedDisease') || ''
);

// State untuk jenis penyakit sementara di dialog
const [tempDisease, setTempDisease] = useState(selectedDisease);

// Handler untuk perubahan jenis penyakit sementara di dialog
const handleTempDiseaseChange = (event) => {
  setTempDisease(event.target.value);
};

// State untuk toggle kode lokasi - ambil dari localStorage jika ada
const [showLocationLabels, setShowLocationLabels] = useState(
  () => localStorage.getItem('showLocationLabels') === 'true' || true
);

// State untuk toggle kode lokasi sementara di dialog
const [tempShowLocationLabels, setTempShowLocationLabels] = useState(showLocationLabels);

// Handler untuk perubahan toggle kode lokasi sementara di dialog
const handleTempShowLocationLabelsChange = (event) => {
  setTempShowLocationLabels(event.target.checked);
};

// State untuk toggle legend - ambil dari localStorage jika ada
const [showLegend, setShowLegend] = useState(
  () => localStorage.getItem('showLegend') === 'true' || true
);

// State untuk toggle legend sementara di dialog
const [tempShowLegend, setTempShowLegend] = useState(showLegend);

// Handler untuk perubahan toggle legend sementara di dialog
const handleTempShowLegendChange = (event) => {
  setTempShowLegend(event.target.checked);
};
  
  // Consume data dari MapDataContext
  const {
    geojsonFeaturesLokasi,
    geojsonFeaturesJalanUtama,
    geojsonFeaturesLebungArea,
    geojsonFeaturesPipaGas,
    geojsonFeaturesPosKeamanan,
    pineappleData,
    bananaData,
    statusColors,
    mandorLocations,
    loading,
    error
  } = useContext(MapDataContext);

  // State for total GeoJSON features (still local for display purposes)
  const [totalGeoJsonFeaturesLokasi, setTotalGeoJsonFeaturesLokasi] = useState(0);

const getFilteredCount = () => {
  if (commodity !== 'Banana') return 0;
  
  let count = 0;
  Object.values(bananaData).forEach(recordsArray => {
    recordsArray.forEach(record => {
      const yearMatch = !selectedYear || record.tahun === selectedYear;
      const statusMatch = !selectedStatus || record.status === selectedStatus;
      
      if (yearMatch && statusMatch) {
        count++;
      }
    });
  });
  
  return count;
};

const getTempFilteredCount = () => {
  if (tempCommodity !== 'Banana') return 0;
  
  let count = 0;
  Object.values(bananaData).forEach(recordsArray => {
    recordsArray.forEach(record => {
      const yearMatch = !tempYear || record.tahun === tempYear;
      const statusMatch = !tempStatus || record.status === tempStatus;
      
      // Filter penyakit jika dipilih
      let diseaseMatch = true;
      if (tempDisease) {
        const diseaseValue = parseFloat(
          tempDisease === 'Panama Disease' ? record.jenis_penyakit?.panama_disease :
          tempDisease === 'Moko' ? record.jenis_penyakit?.moko :
          tempDisease === 'Tip Over' ? record.jenis_penyakit?.tip_over :
          tempDisease === 'Blowdown' ? record.jenis_penyakit?.blowdown : 0
        ) || 0;
        
        diseaseMatch = diseaseValue > 0; // Hanya hitung jika ada nilai penyakit
      }
      
      if (yearMatch && statusMatch && diseaseMatch) {
        count++;
      }
    });
  });
  
  return count;
};

  // Handler untuk tombol settings
  const handleSettingsClick = () => {
    setTempCommodity(commodity); // Set nilai sementara sama dengan nilai saat ini
    setTempYear(selectedYear); // Set tahun sementara sama dengan nilai saat ini
    setSettingsOpen(true);
    console.log('Settings button clicked');
  };

  // Handler untuk menutup settings
  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

// Komponen Legend untuk menampilkan keterangan warna
const Legend = () => {
  const map = useMap();
  
  useEffect(() => {
    // Buat container legend
    const legend = L.control({ position: 'bottomleft' });
    
    legend.onAdd = function () {
      const div = L.DomUtil.create('div', 'legend-container');
      
      // Jika komoditas Banana dan jenis penyakit dipilih
      if (commodity === 'Banana' && selectedDisease) {
        div.innerHTML = `
          <div style="
            background: white;
            padding: 10px;
            border-radius: 5px;
            box-shadow: 0 1px 5px rgba(0,0,0,0.4);
            font-family: Arial, sans-serif;
            font-size: 12px;
          ">
            <h4 style="margin: 0 0 8px 0; text-align: center;">${selectedDisease}</h4>
            <div style="display: flex; align-items: center; margin-bottom: 5px;">
              <div style="width: 20px; height: 20px; background: #92D050; margin-right: 8px; border: 1px solid #666;"></div>
              <span>0-4,99 Ringan</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 5px;">
              <div style="width: 20px; height: 20px; background: #FFFF00; margin-right: 8px; border: 1px solid #666;"></div>
              <span>5,00-9,99 Sedang</span>
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 0;">
              <div style="width: 20px; height: 20px; background: #FF0000; margin-right: 8px; border: 1px solid #666;"></div>
              <span>>10,00 Berat</span>
            </div>
          </div>
        `;
      } 
      // Jika menggunakan warna status (bukan penyakit)
      else if (commodity !== 'None') {
        const statusItems = Object.entries(statusColors).map(([status, color]) => `
          <div style="display: flex; align-items: center; margin-bottom: 5px;">
            <div style="width: 20px; height: 20px; background: ${color}; margin-right: 8px; border: 1px solid #666;"></div>
            <span>${status}</span>
          </div>
        `).join('');
        
        div.innerHTML = `
          <div style="
            background: white;
            padding: 10px;
            border-radius: 5px;
            box-shadow: 0 1px 5px rgba(0,0,0,0.4);
            font-family: Arial, sans-serif;
            font-size: 12px;
          ">
            <h4 style="margin: 0 0 8px 0; text-align: center;">Status</h4>
            ${statusItems}
          </div>
        `;
      }
      // Jika komoditas None, jangan tampilkan legend
      else {
        div.innerHTML = '';
      }
      
      return div;
    };
    
    // Tambahkan legend ke peta jika showLegend true
    if (showLegend) {
      legend.addTo(map);
    }
    
    // Cleanup function
    return () => {
      if (legend && legend.remove) {
        legend.remove();
      }
    };
  }, [map, commodity, selectedDisease, statusColors, showLegend]);
  
  return null;
};

// Handler untuk menyimpan settings - tambahkan toggle legend
const handleSettingsSave = () => {
  setCommodity(tempCommodity);
  setSelectedYear(tempYear);
  setSelectedStatus(tempStatus);
  setSelectedDisease(tempDisease);
  setShowLocationLabels(tempShowLocationLabels);
  setShowLegend(tempShowLegend); // Tambahkan toggle legend
  localStorage.setItem('selectedCommodity', tempCommodity);
  localStorage.setItem('selectedYear', tempYear);
  localStorage.setItem('selectedStatus', tempStatus);
  localStorage.setItem('selectedDisease', tempDisease);
  localStorage.setItem('showLocationLabels', tempShowLocationLabels);
  localStorage.setItem('showLegend', tempShowLegend); // Simpan toggle legend ke localStorage
  setSettingsOpen(false);
};

  // Handler untuk perubahan komoditas sementara di dialog
  const handleTempCommodityChange = (event) => {
    setTempCommodity(event.target.value);
    // Reset tahun jika komoditas diubah dari Banana
    if (event.target.value !== 'Banana') {
      setTempYear('');
    }
  };

  // Handler untuk perubahan tahun sementara di dialog
  const handleTempYearChange = (event) => {
    setTempYear(event.target.value);
  };

  // Effect untuk menghitung filtered count ketika dialog terbuka atau filter berubah
useEffect(() => {
  if (settingsOpen) {
    const count = getTempFilteredCount();
    setTempFilteredCount(count);
  }
}, [settingsOpen, tempCommodity, tempYear, tempStatus, tempDisease, bananaData]);

  useEffect(() => {
    console.log('GeoJSON Features:', geojsonFeaturesLokasi);
    console.log('Number of features:', geojsonFeaturesLokasi.features?.length);
    
    if (geojsonFeaturesLokasi.features) {
      geojsonFeaturesLokasi.features.forEach((feature, index) => {
        console.log(`Feature ${index}:`, feature.properties?.lokasi, feature.geometry.type);
      });
    }
  }, [geojsonFeaturesLokasi]);

  useEffect(() => {
    if (geojsonFeaturesLokasi && geojsonFeaturesLokasi.features) {
      setTotalGeoJsonFeaturesLokasi(geojsonFeaturesLokasi.features.length);
    }
  }, [geojsonFeaturesLokasi]);

// Filter data berdasarkan komoditas yang dipilih - modifikasi untuk Banana
const getFilteredData = () => {
  switch (commodity) {
    case 'Fresh Pineapple':
      return pineappleData;
    case 'Banana':
      const filteredBananaData = {};
      
      Object.keys(bananaData).forEach(lokasiKey => {
        const bananaRecords = bananaData[lokasiKey];
        
        // Cari record yang sesuai dengan filter tahun, status, dan penyakit
        const matchingRecord = bananaRecords.find(record => {
          const yearMatch = !selectedYear || record.tahun === selectedYear;
          const statusMatch = !selectedStatus || record.status === selectedStatus;
          
          // Filter penyakit jika dipilih
          let diseaseMatch = true;
          if (selectedDisease) {
            const diseaseValue = parseFloat(
              selectedDisease === 'Panama Disease' ? record.jenis_penyakit?.panama_disease :
              selectedDisease === 'Moko' ? record.jenis_penyakit?.moko :
              selectedDisease === 'Tip Over' ? record.jenis_penyakit?.tip_over :
              selectedDisease === 'Blowdown' ? record.jenis_penyakit?.blowdown : 0
            ) || 0;
            
            diseaseMatch = diseaseValue > 0; // Hanya tampilkan jika ada nilai penyakit
          }
          
          return yearMatch && statusMatch && diseaseMatch;
        });
        
        // Jika ditemukan record yang sesuai, gunakan yang pertama
        if (matchingRecord) {
          filteredBananaData[lokasiKey] = matchingRecord;
        }
      });
      
      return filteredBananaData;
    case 'None':
    default:
      return {};
  }
};

// Dapatkan daftar status unik dari data banana
const getBananaStatuses = () => {
  const statuses = new Set();
  Object.values(bananaData).forEach(recordsArray => {
    recordsArray.forEach(record => {
      if (record.status) {
        statuses.add(record.status);
      }
    });
  });
  return Array.from(statuses).sort();
};

const bananaStatuses = getBananaStatuses();

// Dapatkan daftar tahun unik dari data banana
const getBananaYears = () => {
  const years = new Set();
  Object.values(bananaData).forEach(recordsArray => {
    recordsArray.forEach(record => {
      if (record.tahun) {
        years.add(record.tahun);
      }
    });
  });
  return Array.from(years).sort();
};

  const bananaYears = getBananaYears();
  const filteredData = getFilteredData();

  // --- onEachFeature functions for different GeoJSON types ---

  // For tb_lokasi
  const onEachFeatureLokasi = useCallback((feature, layer) => {
    const properties = feature.properties || {};
    const lokasiCode = properties.lokasi; 

    // Jika None: hanya info dasar lokasi
    if (commodity === 'None') {
      layer.bindPopup(`<strong>Lokasi:</strong> ${lokasiCode || 'N/A'}`);
      return;
    }

    let popupContent = `<strong>Lokasi:</strong> ${lokasiCode || 'N/A'}<br>`;

    const currentData = filteredData[lokasiCode];
    
    if (currentData) {
      if (commodity === 'Fresh Pineapple') {
        // Tampilkan data pineapple
        popupContent += `<strong>Status:</strong> ${currentData.status || 'N/A'}<br>`;
        popupContent += `<strong>Status Plantation:</strong> ${currentData.status_plantation || 'N/A'}<br>`;
        popupContent += `<strong>Bibit:</strong> ${currentData.bibit || 'N/A'}<br>`;
        popupContent += `<strong>Mulai Tanam:</strong> ${currentData.mulai_tanam || 'N/A'}<br>`;
        popupContent += `<strong>Wilayah:</strong> ${currentData.wilayah || 'N/A'}<br>`;
        popupContent += `<strong>Aktif:</strong> ${currentData.aktif !== undefined ? currentData.aktif : 'N/A'}<br>`;
        popupContent += `<strong>Netto:</strong> ${currentData.netto !== undefined ? currentData.netto : 'N/A'}><br>`;
        popupContent += `<strong>Bruto:</strong> ${currentData.bruto !== undefined ? currentData.bruto : 'N/A'}><br>`;
        popupContent += `<strong>Terakhir Diimpor:</strong> ${currentData.last_imported_at || 'N/A'}<br>`;
        popupContent += `<strong>Mulai Rawat:</strong> ${currentData.mulai_rawat || 'N/A'}><br>`;
        popupContent += `<strong>Perubahan Status:</strong> ${currentData.perubahan_status || 'N/A'}><br>`;
// Di onEachFeatureLokasi untuk Banana
} else if (commodity === 'Banana') {
  // Tampilkan data banana
  popupContent += `<strong>Tahun:</strong> ${currentData.tahun || 'N/A'}<br>`;
  popupContent += `<strong>Status:</strong> ${currentData.status || 'N/A'}<br>`;
  popupContent += `<strong>Ha:</strong> ${currentData.hill || 'N/A'}<br>`;
  popupContent += `<strong>Panama Disease:</strong> ${currentData.jenis_penyakit?.panama_disease || 'N/A'}<br>`;
  popupContent += `<strong>Moko:</strong> ${currentData.jenis_penyakit?.moko || 'N/A'}<br>`;
  popupContent += `<strong>Tip Over:</strong> ${currentData.jenis_penyakit?.tip_over || 'N/A'}<br>`;
  popupContent += `<strong>Blowdown:</strong> ${currentData.jenis_penyakit?.blowdown || 'N/A'}<br>`;
  popupContent += `<strong>Terakhir Diimpor:</strong> ${currentData.last_imported_at || 'N/A'}<br>`;
  
  // Debug info
  popupContent += `<br><em>Debug: Lokasi code: ${lokasiCode}</em>`;
}
    } else {
      popupContent += `<em>Data ${commodity.toLowerCase()} tidak ditemukan atau tidak aktif.</em><br>`; 
    }

    layer.bindPopup(popupContent);
  }, [filteredData, commodity]);

  // For tb_jalan_utama
  const onEachFeatureJalanUtama = useCallback((feature, layer) => {
    const properties = feature.properties || {}; // Safely get properties
    const idJalan = properties.Id || 'N/A';
    const panjang = properties.Panjang || 'N/A';
    const ket = properties.Ket || 'N/A';
    const type = feature.geometry?.type || 'N/A';

    let popupContent = `<strong>Jalan Utama ID:</strong> ${idJalan}<br>`;
    popupContent += `<strong>Panjang:</strong> ${panjang} m<br>`;
    popupContent += `<strong>Keterangan:</strong> ${ket}<br>`;
    popupContent += `<strong>Tipe Geometri:</strong> ${type}<br>`;
    layer.bindPopup(popupContent);
  }, []);

  // For tb_lebung_area
  const onEachFeatureLebungArea = useCallback((feature, layer) => {
    const properties = feature.properties || {}; // Safely get properties
    const id = properties.Id || 'N/A'; 
    const luas = properties.luas || 'N/A';
    const type = feature.geometry?.type || 'N/A';

    let popupContent = `<strong>ID Lebung:</strong> ${id}<br>`;
    popupContent += `<strong>Luas:</strong> ${luas} ha<br>`;
    popupContent += `<strong>Tipe Geometri:</strong> ${type}<br>`;
    layer.bindPopup(popupContent);
  }, []);

  // For tb_pipa_gas
  const onEachFeaturePipaGas = useCallback((feature, layer) => {
    const properties = feature.properties || {}; // Safely get properties
    const id = properties.Id || 'N/A'; 
    const type = feature.geometry?.type || 'N/A';

    let popupContent = `<strong>ID Pipa Gas:</strong> ${id}<br>`;
    popupContent += `<strong>Tipe Geometri:</strong> ${type}<br>`;
    layer.bindPopup(popupContent);
  }, []);

  // For tb_pos_keamanan
  const onEachFeaturePosKeamanan = useCallback((feature, layer) => {
    const properties = feature.properties || {}; // Safely get properties
    const id = properties.Id || 'N/A'; 
    const namaPos = properties.Pos || 'N/A'; 
    const type = feature.geometry?.type || 'N/A';

    let popupContent = `<strong>ID Pos:</strong> ${id}<br>`;
    popupContent += `<strong>Nama Pos:</strong> ${namaPos}<br>`;
    popupContent += `<strong>Tipe Geometri:</strong> ${type}<br>`;
    layer.bindPopup(popupContent);
  }, []);

  // --- styleGeoJSON functions for different GeoJSON types ---

useEffect(() => {
  console.log('Banana Data:', bananaData);
  console.log('Filtered Data:', getFilteredData());
  console.log('Filter Criteria - Year:', selectedYear, 'Status:', selectedStatus);
  console.log('GeoJSON Features Count:', geojsonFeaturesLokasi.features?.length);
}, [bananaData, selectedYear, selectedStatus, commodity]);

// --- styleGeoJSON function untuk tb_lokasi ---
const styleGeoJSONLokasi = useCallback((feature) => {
  const properties = feature.properties || {};
  const lokasiCode = properties.lokasi; 
  const currentData = filteredData[lokasiCode];
  
  console.log(`Styling feature ${lokasiCode}:`, currentData);

  const defaultColor = '#808080'; // Default gray untuk inactive/not found

  let fillColor = defaultColor;
  let weight = 2; 
  let opacity = 0.8; 
  let fillOpacity = 0.5; 

  // Jika None: tanpa fill color
  if (commodity === 'None') {
    return {
      fillOpacity: 0,   // tidak ada pewarnaan
      color: '#000000', // garis tepi saja
      weight: 1.5,
      opacity: 0.8
    };
  }

  // Jika komoditas Banana dan jenis penyakit dipilih
  if (commodity === 'Banana' && selectedDisease && currentData && currentData.jenis_penyakit) {
    let diseaseValue = 0;
    
    // Ambil nilai penyakit yang dipilih
    switch(selectedDisease) {
      case 'Panama Disease':
        diseaseValue = parseFloat(currentData.jenis_penyakit.panama_disease) || 0;
        break;
      case 'Moko':
        diseaseValue = parseFloat(currentData.jenis_penyakit.moko) || 0;
        break;
      case 'Tip Over':
        diseaseValue = parseFloat(currentData.jenis_penyakit.tip_over) || 0;
        break;
      case 'Blowdown':
        diseaseValue = parseFloat(currentData.jenis_penyakit.blowdown) || 0;
        break;
      default:
        diseaseValue = 0;
    }
    
    // Tentukan warna berdasarkan nilai penyakit
    if (diseaseValue >= 0 && diseaseValue <= 4.99) {
      fillColor = '#92D050'; // Hijau
    } else if (diseaseValue >= 5.00 && diseaseValue <= 9.99) {
      fillColor = '#FFFF00'; // Kuning
    } else if (diseaseValue >= 10.00) {
      fillColor = '#FF0000'; // Merah
    }
  } 
  // Jika tidak memilih jenis penyakit atau bukan Banana, gunakan warna status
  else if (currentData && currentData.status) { 
    fillColor = statusColors[currentData.status] || defaultColor;
  }

  return {
    fillColor: fillColor,
    weight: weight,
    opacity: opacity,
    color: 'black', 
    fillOpacity: fillOpacity,
  };
}, [filteredData, statusColors, commodity, selectedDisease]);

  // For tb_jalan_utama
  const styleGeoJSONJalanUtama = useCallback(() => {
    return {
      color: '#8B4513', // SaddleBrown for roads
      weight: 3,
      opacity: 0.9,
    };
  }, []);

  // For tb_lebung_area
  const styleGeoJSONLebungArea = useCallback(() => {
    return {
      fillColor: '#ADD8E6', // LightBlue
      color: '#0000FF', // Blue border
      weight: 1,
      opacity: 0.7,
      fillOpacity: 0.6,
    };
  }, []);

  // For tb_pipa_gas
  const styleGeoJSONPipaGas = useCallback(() => {
    return {
      color: '#FF0000', // Red for gas pipes
      weight: 2,
      dashArray: '5, 5', // Dashed line
      opacity: 0.7,
    };
  }, []);

  // Komponen pembantu untuk menyesuaikan tampilan peta
  const FitBounds = ({ geojson }) => {
    const map = useMap();
    useEffect(() => {
      if (geojson && geojson.features && geojson.features.length > 0) {
        const bounds = L.geoJSON(geojson).getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds);
        }
      }
    }, [map, geojson]);
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Memuat data peta...</Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    );
  }

  // Display upload interface if no GeoJSON is loaded and there's an error (REMOVED FROM HERE)
  if (error && geojsonFeaturesLokasi.features.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Typography variant="body1" sx={{ mt: 2 }}>
          Silakan hubungi administrator untuk mengunggah atau mengelola data GeoJSON di halaman Manajemen GeoJSON.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="peta-estate-container">

      <MapContainer 
        center={[-5.0, 105.6]}
        zoom={12} 
        style={{ height: '100%', width: '100%', borderRadius: '8px', overflow: 'hidden' }} 
        scrollWheelZoom={true}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Komponen Legend */}
  {showLegend && <Legend />}
        
        {/* Custom zoom control dengan tombol settings */}
        <CustomZoomControl onSettingsClick={handleSettingsClick} />
        
        {/* Komponen untuk menampilkan label lokasi dengan LayerGroup */}
<LocationLabelsWithLayerGroup 
  geojsonFeatures={geojsonFeaturesLokasi} 
  showLabels={showLocationLabels}
/>
        
        {/* Di dalam komponen PetaEstate4, tambahkan pointToLayer untuk GeoJSON*/}
        {geojsonFeaturesLokasi.features && geojsonFeaturesLokasi.features.length > 0 && (
          <GeoJSON 
            data={geojsonFeaturesLokasi} 
            onEachFeature={onEachFeatureLokasi} 
            style={styleGeoJSONLokasi}
            pointToLayer={(feature, latlng) => {
              const properties = feature.properties || {};
              const lokasiCode = properties.lokasi;
              
              // Untuk point features, buat label
              if (feature.geometry.type === 'Point' && lokasiCode) {
                return L.marker(latlng, {
                  icon: L.divIcon({
                    html: `<div style="
                      background: transparent !important;
                      padding: 4px 8px;
                      border-radius: 4px;
                      font-size: 12px;
                      font-weight: bold;
                      border: 1px solid #666;
                      white-space: nowrap;
                      text-align: center;
                      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                      pointer-events: none;
                    ">${lokasiCode}</div>`,
                    className: 'location-label-icon',
                    iconSize: [null, null],
                    iconAnchor: [0, 0]
                  }),
                  interactive: false,
                  zIndexOffset: 1000
                });
              }
              
              // Untuk geometri lainnya, gunakan default
              return L.geoJSON(feature);
            }}
          />
        )}

        {/* Render GeoJSON lainnya */}
        {geojsonFeaturesJalanUtama.features && geojsonFeaturesJalanUtama.features.length > 0 && (
          <GeoJSON 
            data={geojsonFeaturesJalanUtama} 
            onEachFeature={onEachFeatureJalanUtama} 
            style={styleGeoJSONJalanUtama} 
          />
        )}

        {geojsonFeaturesLebungArea.features && geojsonFeaturesLebungArea.features.length > 0 && (
          <GeoJSON 
            data={geojsonFeaturesLebungArea} 
            onEachFeature={onEachFeatureLebungArea} 
            style={styleGeoJSONLebungArea} 
          />
        )}

        {geojsonFeaturesPipaGas.features && geojsonFeaturesPipaGas.features.length > 0 && (
          <GeoJSON 
            data={geojsonFeaturesPipaGas} 
            onEachFeature={onEachFeaturePipaGas} 
            style={styleGeoJSONPipaGas} 
          />
        )}

        {/* Render markers for tb_pos_keamanan */}
        {geojsonFeaturesPosKeamanan.features && geojsonFeaturesPosKeamanan.features.length > 0 && (
          geojsonFeaturesPosKeamanan.features.map((feature, index) => {
            if (feature.geometry && feature.geometry.type === 'Point' && feature.geometry.coordinates) {
              const [lng, lat] = feature.geometry.coordinates;
              return (
                <Marker 
                  key={`pos-keamanan-${index}`} 
                  position={[lat, lng]} 
                  icon={securityPostIcon}
                >
                  <Popup>
                    <strong>Pos Keamanan:</strong> {feature.properties.Pos || 'N/A'}<br/>
                    <strong>ID:</strong> {feature.properties.Id || 'N/A'}<br/>
                  </Popup>
                </Marker>
              );
            }
            return null;
          })
        )}

        

        {/* Adjust map bounds */}
        <FitBounds geojson={geojsonFeaturesLokasi} />

        {/* Render foreman locations */}
        {mandorLocations.map((mandor) => (
          <Marker 
            key={mandor.id} 
            position={[mandor.lat, mandor.lng]} 
            icon={mandorIcon}
          >
            <Popup>
              <strong>{mandor.name}</strong><br/>
              Lat: {mandor.lat.toFixed(4)}, Lng: {mandor.lng.toFixed(4)}<br/>
              Terakhir diperbarui: {new Date(mandor.timestamp).toLocaleString()}
            </Popup>
          </Marker>
        ))}
      </MapContainer>

{/* Dialog Settings dengan Slide Transition */}
<Dialog
  open={settingsOpen}
  slots={{
    transition: Transition,
  }}
  keepMounted
  onClose={handleSettingsClose}
  aria-describedby="alert-dialog-slide-description"
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>{"Pengaturan Peta"}</DialogTitle>

  <DialogContent>
    <DialogContentText id="alert-dialog-slide-description" sx={{ mb: 2 }}>
      Atur preferensi tampilan peta di sini.
    </DialogContentText>

    {/* Switch iOS style untuk toggle kode lokasi */}
    <FormControlLabel
      control={
        <Switch
          checked={tempShowLocationLabels}
          onChange={handleTempShowLocationLabelsChange}
          name="showLocationLabels"
          color="primary"
        />
      }
      label="Tampilkan kode lokasi"
      sx={{ mb: 2 }}
    />
    
    {/* Dropdown untuk memilih komoditas */}
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel id="commodity-select-label">Komoditas</InputLabel>
      <Select
        labelId="commodity-select-label"
        id="commodity-select"
        value={tempCommodity}
        label="Komoditas"
        onChange={handleTempCommodityChange}
        size="small"
      >
        <MenuItem value="None">None</MenuItem>
        <MenuItem value="Fresh Pineapple">Fresh Pineapple</MenuItem>
        <MenuItem value="Banana">Banana</MenuItem>
      </Select>
    </FormControl>

    {/* Dropdown untuk memilih tahun (hanya tampil jika komoditas Banana dipilih) */}
    {tempCommodity === 'Banana' && (
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="year-select-label">Tahun</InputLabel>
        <Select
          labelId="year-select-label"
          id="year-select"
          value={tempYear}
          label="Tahun"
          onChange={handleTempYearChange}
          size="small"
        >
          <MenuItem value="">
            <em>Semua Tahun</em>
          </MenuItem>
          {bananaYears.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )}

    {/* Dropdown untuk memilih status (hanya tampil jika komoditas Banana dipilih) */}
    {tempCommodity === 'Banana' && (
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="status-select-label">Status</InputLabel>
        <Select
          labelId="status-select-label"
          id="status-select"
          value={tempStatus}
          label="Status"
          onChange={handleTempStatusChange}
          size="small"
        >
          <MenuItem value="">
            <em>Semua Status</em>
          </MenuItem>
          {bananaStatuses.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )}

{tempCommodity === 'Banana' && (
  <FormControl fullWidth sx={{ mb: 2 }}>
    <InputLabel id="disease-select-label">Jenis Penyakit</InputLabel>
    <Select
      labelId="disease-select-label"
      id="disease-select"
      value={tempDisease}
      label="Jenis Penyakit"
      onChange={handleTempDiseaseChange}
      size="small"
    >
      <MenuItem value="">
        <em>Semua Jenis Penyakit</em>
      </MenuItem>
      <MenuItem value="Panama Disease">Panama Disease</MenuItem>
      <MenuItem value="Moko">Moko</MenuItem>
      <MenuItem value="Tip Over">Tip Over</MenuItem>
      <MenuItem value="Blowdown">Blowdown</MenuItem>
    </Select>
  </FormControl>
)}

{/* Switch iOS style untuk toggle legend */}
{tempCommodity === 'Banana' && (
    <FormControlLabel
      control={
        <Switch
          checked={tempShowLegend}
          onChange={handleTempShowLegendChange}
          name="showLegend"
          color="primary"
        />
      }
      label="Tampilkan legend"
      sx={{ mb: 2 }}
    />
)}

    {/* Tampilkan filtered count di bawah status */}
    {tempCommodity === 'Banana' && (
      <Box sx={{ mt: 1, mb: 1 }}>
        <Typography variant="body2" color="textSecondary">
          Jumlah data yang sesuai filter: <strong>{tempFilteredCount}</strong>
        </Typography>
      </Box>
    )}
  </DialogContent>

  <DialogActions>
    <Button onClick={handleSettingsClose}>Tutup</Button>
    <Button onClick={handleSettingsSave} variant="contained" color="primary">
      Simpan
    </Button>
  </DialogActions>
</Dialog>

    </Box>
  );
}

export default PetaEstate4;